import type { DocumentContent, UserPreferences } from '../types';

// Storage keys with consistent prefix
const STORAGE_KEYS = {
  THEME: 'content-studio-theme',
  PANEL_SPLIT: 'content-studio-panel-split',
  DOCUMENT: 'content-studio-document',
  HISTORY: 'content-studio-history',
  PREFERENCES: 'content-studio-preferences',
  VERSION: 'content-studio-version',
} as const;

// Current storage version for migration
const CURRENT_VERSION = '1.0.0';

// Maximum number of documents in history
const MAX_HISTORY_LENGTH = 10;

// Estimated storage quota warning threshold (in bytes)
const STORAGE_WARNING_THRESHOLD = 4.5 * 1024 * 1024; // 4.5MB (LocalStorage is typically 5MB)

export interface StoredDocument extends DocumentContent {
  savedAt: string; // ISO string
}

export interface DocumentHistory {
  documents: StoredDocument[];
  lastAccessed: string;
}

export interface StorageStats {
  totalSize: number;
  documentCount: number;
  historyCount: number;
  isNearLimit: boolean;
  lastCleanup?: string;
}

export interface ExportData {
  version: string;
  exportedAt: string;
  document?: StoredDocument;
  history: StoredDocument[];
  preferences: UserPreferences;
}

class PersistenceService {
  constructor() {
    this.initialize();
  }

  /**
   * Initialize storage and perform any necessary migrations
   */
  private initialize(): void {
    try {
      this.performMigrations();
    } catch (error) {
      console.error('Failed to initialize persistence service:', error);
    }
  }

  /**
   * Perform data migrations between versions
   */
  private performMigrations(): void {
    const currentVersion = this.getStoredVersion();
    
    if (!currentVersion || currentVersion !== CURRENT_VERSION) {
      // Perform any necessary data migrations here
      this.setStoredVersion(CURRENT_VERSION);
      console.log(`Storage migrated to version ${CURRENT_VERSION}`);
    }
  }

  /**
   * Save current document with auto-save functionality
   */
  saveDocument(document: DocumentContent): void {
    try {
      const storedDoc: StoredDocument = {
        ...document,
        savedAt: new Date().toISOString(),
      };

      localStorage.setItem(STORAGE_KEYS.DOCUMENT, JSON.stringify(storedDoc));
    } catch (error) {
      console.error('Failed to save document:', error);
      throw new Error('Failed to save document. Storage may be full.');
    }
  }

  /**
   * Load the current document
   */
  loadDocument(): StoredDocument | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.DOCUMENT);
      if (!stored) return null;

      const document = JSON.parse(stored) as StoredDocument;
      return this.validateDocument(document) ? document : null;
    } catch (error) {
      console.error('Failed to load document:', error);
      return null;
    }
  }

  /**
   * Add document to history (called when switching documents or on significant saves)
   */
  addToHistory(document: DocumentContent): void {
    try {
      const history = this.getHistory();
      const storedDoc: StoredDocument = {
        ...document,
        savedAt: new Date().toISOString(),
      };

      // Remove any existing version of this document from history
      history.documents = history.documents.filter(doc => doc.id !== document.id);

      // Add to beginning of history
      history.documents.unshift(storedDoc);

      // Limit history length
      if (history.documents.length > MAX_HISTORY_LENGTH) {
        history.documents = history.documents.slice(0, MAX_HISTORY_LENGTH);
      }

      history.lastAccessed = new Date().toISOString();

      localStorage.setItem(STORAGE_KEYS.HISTORY, JSON.stringify(history));
    } catch (error) {
      console.error('Failed to add document to history:', error);
      // Don't throw here - history is not critical functionality
    }
  }

  /**
   * Get document history
   */
  getHistory(): DocumentHistory {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.HISTORY);
      if (!stored) {
        return { documents: [], lastAccessed: new Date().toISOString() };
      }

      const history = JSON.parse(stored) as DocumentHistory;
      
      // Validate and clean up history
      history.documents = history.documents
        .filter(doc => this.validateDocument(doc))
        .slice(0, MAX_HISTORY_LENGTH);

      return history;
    } catch (error) {
      console.error('Failed to load document history:', error);
      return { documents: [], lastAccessed: new Date().toISOString() };
    }
  }

  /**
   * Save user preferences (extended beyond just theme/panels)
   */
  savePreferences(preferences: Partial<UserPreferences>): void {
    try {
      const existing = this.loadPreferences();
      const updated = { ...existing, ...preferences };
      
      localStorage.setItem(STORAGE_KEYS.PREFERENCES, JSON.stringify(updated));

      // Also save individual keys for backward compatibility
      if (preferences.theme) {
        localStorage.setItem(STORAGE_KEYS.THEME, preferences.theme);
      }
      if (preferences.panelSplit !== undefined) {
        localStorage.setItem(STORAGE_KEYS.PANEL_SPLIT, preferences.panelSplit.toString());
      }
    } catch (error) {
      console.error('Failed to save preferences:', error);
      throw new Error('Failed to save preferences.');
    }
  }

  /**
   * Load user preferences
   */
  loadPreferences(): UserPreferences {
    try {
      // Try loading from new consolidated preferences
      const stored = localStorage.getItem(STORAGE_KEYS.PREFERENCES);
      if (stored) {
        const preferences = JSON.parse(stored) as UserPreferences;
        return this.validatePreferences(preferences);
      }

      // Fall back to individual keys for backward compatibility
      const theme = (localStorage.getItem(STORAGE_KEYS.THEME) as 'light' | 'dark' | 'system') || 'system';
      const panelSplit = parseInt(localStorage.getItem(STORAGE_KEYS.PANEL_SPLIT) || '50', 10);

      const defaultPreferences: UserPreferences = {
        theme,
        panelSplit,
        editorFontSize: 14,
        previewFontSize: 16,
        autoSave: true,
        exportDefaults: {
          format: 'html',
          includeCSS: true,
        },
      };

      // Save consolidated preferences for next time
      this.savePreferences(defaultPreferences);
      return defaultPreferences;
    } catch (error) {
      console.error('Failed to load preferences:', error);
      return this.getDefaultPreferences();
    }
  }

  /**
   * Export all data for backup
   */
  exportData(): ExportData {
    const currentDoc = this.loadDocument();
    const history = this.getHistory();
    const preferences = this.loadPreferences();

    return {
      version: CURRENT_VERSION,
      exportedAt: new Date().toISOString(),
      document: currentDoc || undefined,
      history: history.documents,
      preferences,
    };
  }

  /**
   * Import data from backup
   */
  importData(data: ExportData): void {
    try {
      // Validate import data
      if (!this.validateImportData(data)) {
        throw new Error('Invalid import data format');
      }

      // Import preferences
      this.savePreferences(data.preferences);

      // Import current document if available
      if (data.document) {
        this.saveDocument(data.document);
      }

      // Import history
      if (data.history && data.history.length > 0) {
        const history: DocumentHistory = {
          documents: data.history.slice(0, MAX_HISTORY_LENGTH),
          lastAccessed: new Date().toISOString(),
        };
        localStorage.setItem(STORAGE_KEYS.HISTORY, JSON.stringify(history));
      }

    } catch (error) {
      console.error('Failed to import data:', error);
      throw new Error('Failed to import data. Please check the file format.');
    }
  }

  /**
   * Get storage statistics
   */
  getStorageStats(): StorageStats {
    let totalSize = 0;
    const documentCount = this.loadDocument() ? 1 : 0;
    const historyCount = this.getHistory().documents.length;

    // Calculate approximate storage usage
    try {
      Object.values(STORAGE_KEYS).forEach(key => {
        const item = localStorage.getItem(key);
        if (item) {
          totalSize += item.length * 2; // Approximate bytes (UTF-16)
        }
      });
    } catch (error) {
      console.error('Failed to calculate storage stats:', error);
    }

    return {
      totalSize,
      documentCount,
      historyCount,
      isNearLimit: totalSize > STORAGE_WARNING_THRESHOLD,
      lastCleanup: localStorage.getItem('content-studio-last-cleanup') || undefined,
    };
  }

  /**
   * Clean up old data to free storage space
   */
  cleanupStorage(): void {
    try {
      const history = this.getHistory();
      
      // Keep only the 5 most recent documents in history during cleanup
      if (history.documents.length > 5) {
        history.documents = history.documents.slice(0, 5);
        localStorage.setItem(STORAGE_KEYS.HISTORY, JSON.stringify(history));
      }

      // Mark cleanup time
      localStorage.setItem('content-studio-last-cleanup', new Date().toISOString());

      console.log('Storage cleanup completed');
    } catch (error) {
      console.error('Failed to cleanup storage:', error);
    }
  }

  /**
   * Clear all data (for reset functionality)
   */
  clearAllData(): void {
    try {
      Object.values(STORAGE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
      localStorage.removeItem('content-studio-last-cleanup');
      console.log('All data cleared');
    } catch (error) {
      console.error('Failed to clear data:', error);
    }
  }

  // Private helper methods

  private getStoredVersion(): string | null {
    return localStorage.getItem(STORAGE_KEYS.VERSION);
  }

  private setStoredVersion(version: string): void {
    localStorage.setItem(STORAGE_KEYS.VERSION, version);
  }

  private validateDocument(doc: any): doc is StoredDocument {
    return (
      doc &&
      typeof doc.id === 'string' &&
      typeof doc.content === 'string' &&
      typeof doc.title === 'string' &&
      typeof doc.savedAt === 'string' &&
      typeof doc.wordCount === 'number'
    );
  }

  private validatePreferences(prefs: any): UserPreferences {
    const defaults = this.getDefaultPreferences();
    
    return {
      theme: ['light', 'dark', 'system'].includes(prefs.theme) ? prefs.theme : defaults.theme,
      panelSplit: typeof prefs.panelSplit === 'number' && prefs.panelSplit >= 10 && prefs.panelSplit <= 90 
        ? prefs.panelSplit : defaults.panelSplit,
      editorFontSize: typeof prefs.editorFontSize === 'number' ? prefs.editorFontSize : defaults.editorFontSize,
      previewFontSize: typeof prefs.previewFontSize === 'number' ? prefs.previewFontSize : defaults.previewFontSize,
      autoSave: typeof prefs.autoSave === 'boolean' ? prefs.autoSave : defaults.autoSave,
      exportDefaults: prefs.exportDefaults || defaults.exportDefaults,
    };
  }

  private validateImportData(data: any): data is ExportData {
    return (
      data &&
      typeof data.version === 'string' &&
      typeof data.exportedAt === 'string' &&
      data.preferences &&
      Array.isArray(data.history)
    );
  }

  private getDefaultPreferences(): UserPreferences {
    return {
      theme: 'system',
      panelSplit: 50,
      editorFontSize: 14,
      previewFontSize: 16,
      autoSave: true,
      exportDefaults: {
        format: 'html',
        includeCSS: true,
      },
    };
  }
}

// Export singleton instance
export const persistenceService = new PersistenceService();